/**
 * Centralized feature flags — dynamic, PostHog-backed (ENG-2739).
 *
 * Resolution precedence:
 *   1. Live PostHog updates while the app is open (see plugins/posthog.js).
 *   2. Backend-evaluated bootstrap fetched before mount (hydrateFeatureFlags).
 *   3. Compiled VITE_* defaults — local dev (no PostHog) and offline fallback.
 *
 * Flag keys are the PostHog keys; the backend registry lives in
 * apps/backend/valur/feature_flags.py and must stay in sync.
 *
 * Usage:
 *   import { FLAG_HAMILTON, isFeatureEnabled } from '@/store/feature_flags';
 *   if (isFeatureEnabled(FLAG_HAMILTON)) { ... }
 *   // or use feature_flags_mixin for component access
 */
import axios from 'axios';
import { reactive } from 'vue';

import { API_ROOT } from '@/store/api';

// Flag keys are flat in PostHog (kebab-case only), so hierarchy lives in the
// key itself: '<domain>-<feature>', e.g. 'profile-change-password'. Group
// constants by domain below; a bare domain key (e.g. 'documents') gates the
// whole domain. Keep names in sync with the FEATURE_* constants in
// apps/backend/valur/feature_flags.py.

// -- documents: unified documents browser
export const FLAG_DOCUMENTS = 'documents';
// -- authentication: sign-in surfaces. Two-factor has no flag: the policy in
// Operations → Platform settings (and Area 51) decides who has it (ENG-3380).
export const FLAG_AUTHENTICATION_GOOGLE_LOGIN = 'authentication-google-login';
// -- hamilton
export const FLAG_HAMILTON = 'hamilton';
export const FLAG_HAMILTON_TRUST_CHAT = 'hamilton-trust-chat';
// -- profile: account self-service
export const FLAG_PROFILE_CHANGE_PASSWORD = 'profile-change-password';
// -- tax season
export const FLAG_TAX_DOCUMENT_COLLECTION = 'tax-document-collection';
export const FLAG_TAX_SEASON_COLLECTION_ADVISORS = 'tax-season-collection-advisors';
export const FLAG_GIFT_TAX_QUESTIONNAIRE = 'tax-season-gift-tax-questionnaire';
export const FLAG_GIFT_TAX_QUESTIONNAIRE_ADVISORS = 'tax-season-gift-tax-questionnaire-advisors';
// -- advisor: self-registration & trial onboarding
export const FLAG_ADVISOR_SELF_REGISTRATION = 'advisor-self-registration';
// -- advisor: multi-advisor client sharing (ENG-2789) — workspace share-all +
// per-member book sharing + shared-access surfaces (Clients scope filter,
// member drawer sharing block). Assigned-advisor access is never flagged.
export const FLAG_ADVISOR_CLIENT_SHARING = 'advisor-client-sharing';
// -- advisor: hand out an advisor seat by email (ENG-3046) — gates creating an
// account for an unregistered address, on the portal's Invite member and the ops
// Partners drawer. Adding an existing account, resending, the trial levers and
// everything after the invite (set password, activation, access) stay ungated.
export const FLAG_ADVISOR_INVITE_ADVISOR = 'advisor-invite-advisor';
// -- tasks: the general tasks system (ENG-2809 / ENG-2845) — gates the dashboard
// Agenda sidebar and every /tasks/ call (the backend refuses when off, so this
// is a master switch, not just hidden UI).
export const FLAG_CLIENT_TASKS = 'client-tasks';
// -- trust: live accounting summary, details, and withdrawal planning (ENG-3000)
export const FLAG_TRUST_DASHBOARD_ACCOUNTING = 'trust-dashboard-accounting';
// -- calculators: CRUT trust-length options
export const FLAG_CALCULATORS_CRUT_TWO_LIVES = 'calculators-crut-two-lives';
export const FLAG_CALCULATORS_CRUT_HEDGED = 'calculators-crut-hedged';
// -- layout: serve the app below the `lg` breakpoint (ENG-2840) — moves the
// small-screen floor from 1024px to 748px and collapses the sidebar rail across
// lg–xl so the content column doesn't shrink as the tier steps up. Off = the
// layout production shipped with, exactly.
export const FLAG_LAYOUT_NARROW_VIEWPORT = 'layout-narrow-viewport';

// -- ops: document validation (ENG-2821) — kill switch on the ops-facing surfaces
// (the /document-validation/ API and the Validation keys card). The seasonal
// send-command cross-check is deliberately NOT flagged.
export const FLAG_OPS_DOCUMENT_VALIDATION = 'ops-document-validation';
// -- ops: Form 5227 extraction (ENG-2894) — gates the staff navigation,
// routes, and API independently from document validation.
export const FLAG_OPS_FORM_5227_EXTRACTION = 'ops-form-5227-extraction';
// -- ops: custodian statement ingestion (ENG-2999) — gates the staff navigation,
// routes, and API for reviewing brokerage statements into trust valuations.
export const FLAG_OPS_CUSTODIAN_STATEMENTS = 'ops-custodian-statements';

export const FLAG_CALCULATORS_OPS_DASHBOARD = 'calculators-ops-dashboard';

// -- calculators (ENG-2841): the bare domain key kills every calculator surface
// at once; per-calculator keys kill one calculator (nav entry, hub tile, routes,
// /shared/ links). All kill switches on live public pages → default ON.
export const FLAG_CALCULATORS = 'calculators';
export const FLAG_CALCULATORS_CRUT = 'calculators-crut';
export const FLAG_CALCULATORS_CLAT = 'calculators-clat';
export const FLAG_CALCULATORS_GRAT = 'calculators-grat';
export const FLAG_CALCULATORS_QSBS = 'calculators-qsbs';
export const FLAG_CALCULATORS_SOLAR = 'calculators-solar';
export const FLAG_CALCULATORS_IRA = 'calculators-ira';
export const FLAG_CALCULATORS_OIL_GAS = 'calculators-oil-gas';
export const FLAG_CALCULATORS_PPLI = 'calculators-ppli';
export const FLAG_CALCULATORS_NGT = 'calculators-ngt';
export const FLAG_CALCULATORS_ESTATE_TAX_BURN = 'calculators-estate-tax-burn';
// The comparison group switch kills all three comparison pages at once; the
// per-page keys kill one page each (route, hub CTA tile, model endpoint).
export const FLAG_CALCULATORS_COMPARISON = 'calculators-comparison';
export const FLAG_CALCULATORS_COMPARISON_CAPITAL_GAINS = 'calculators-comparison-capital-gains';
export const FLAG_CALCULATORS_COMPARISON_ORDINARY_INCOME = 'calculators-comparison-ordinary-income';
export const FLAG_CALCULATORS_COMPARISON_ESTATE = 'calculators-comparison-estate';

// Compiled fallbacks: default-on unless the env explicitly says 'false'
// (kill-switch semantics preserved from the static implementation).
// Unlaunched gates (authentication-google-login) rely on an explicit =false in every .env.
const DEFAULTS = {
    [FLAG_DOCUMENTS]: import.meta.env.VITE_DOCUMENTS_ENABLED !== 'false',
    [FLAG_AUTHENTICATION_GOOGLE_LOGIN]: import.meta.env.VITE_GOOGLE_LOGIN_ENABLED !== 'false',
    [FLAG_HAMILTON]: import.meta.env.VITE_HAMILTON_ENABLED !== 'false',
    [FLAG_HAMILTON_TRUST_CHAT]: import.meta.env.VITE_HAMILTON_TRUST_CHAT_ENABLED !== 'false',
    [FLAG_PROFILE_CHANGE_PASSWORD]: import.meta.env.VITE_PROFILE_CHANGE_PASSWORD_ENABLED !== 'false',
    [FLAG_TAX_DOCUMENT_COLLECTION]: import.meta.env.VITE_TAX_DOCUMENT_COLLECTION_ENABLED !== 'false',
    [FLAG_TAX_SEASON_COLLECTION_ADVISORS]: import.meta.env.VITE_TAX_SEASON_COLLECTION_ADVISORS_ENABLED !== 'false',
    // Default-OFF (seasonal window switch): on only when the env explicitly says 'true'.
    [FLAG_GIFT_TAX_QUESTIONNAIRE]: import.meta.env.VITE_GIFT_TAX_QUESTIONNAIRE_ENABLED === 'true',
    [FLAG_GIFT_TAX_QUESTIONNAIRE_ADVISORS]: import.meta.env.VITE_GIFT_TAX_QUESTIONNAIRE_ADVISORS_ENABLED === 'true',
    // Unlaunched → opt-in (default off); PostHog / backend bootstrap governs real envs.
    [FLAG_ADVISOR_SELF_REGISTRATION]: import.meta.env.VITE_ADVISOR_SELF_REGISTRATION_ENABLED === 'true',
    // Unlaunched → opt-in (default off); PostHog / backend bootstrap governs real envs.
    [FLAG_ADVISOR_CLIENT_SHARING]: import.meta.env.VITE_ADVISOR_CLIENT_SHARING_ENABLED === 'true',
    // Unlaunched → opt-in (default off); PostHog / backend bootstrap governs real envs.
    [FLAG_ADVISOR_INVITE_ADVISOR]: import.meta.env.VITE_ADVISOR_INVITE_ADVISOR_ENABLED === 'true',
    // Unlaunched → opt-in (default off); PostHog / backend bootstrap governs real envs.
    [FLAG_CLIENT_TASKS]: import.meta.env.VITE_CLIENT_TASKS_ENABLED === 'true',
    [FLAG_TRUST_DASHBOARD_ACCOUNTING]: import.meta.env.VITE_TRUST_DASHBOARD_ACCOUNTING_ENABLED === 'true',
    // Unlaunched → opt-in (default off); PostHog / backend bootstrap governs real envs.
    [FLAG_CALCULATORS_CRUT_TWO_LIVES]: import.meta.env.VITE_CALCULATORS_CRUT_TWO_LIVES_ENABLED === 'true',
    [FLAG_CALCULATORS_CRUT_HEDGED]: import.meta.env.VITE_CALCULATORS_CRUT_HEDGED_ENABLED === 'true',
    // Unlaunched → opt-in (default off). Fail-safe off matches the backend: an ops surface that
    // reads client data should disappear during an outage, not keep serving.
    [FLAG_OPS_DOCUMENT_VALIDATION]: import.meta.env.VITE_OPS_DOCUMENT_VALIDATION_ENABLED === 'true',
    // Same, except in local development: on unless the env says 'false', so a local .env that
    // predates the line still shows the tool. Mirrors the backend's ENVIRONMENT-based default.
    [FLAG_OPS_FORM_5227_EXTRACTION]:
        import.meta.env.VITE_ENVIRONMENT === 'development'
            ? import.meta.env.VITE_OPS_FORM_5227_EXTRACTION_ENABLED !== 'false'
            : import.meta.env.VITE_OPS_FORM_5227_EXTRACTION_ENABLED === 'true',
    [FLAG_OPS_CUSTODIAN_STATEMENTS]:
        import.meta.env.VITE_ENVIRONMENT === 'development'
            ? import.meta.env.VITE_OPS_CUSTODIAN_STATEMENTS_ENABLED !== 'false'
            : import.meta.env.VITE_OPS_CUSTODIAN_STATEMENTS_ENABLED === 'true',
    [FLAG_CALCULATORS_OPS_DASHBOARD]: import.meta.env.VITE_CALCULATORS_OPS_DASHBOARD_ENABLED === 'true',
    // Unlaunched → opt-in (default off), i.e. the 1024px floor production shipped with.
    [FLAG_LAYOUT_NARROW_VIEWPORT]: import.meta.env.VITE_LAYOUT_NARROW_VIEWPORT_ENABLED === 'true',
    // Kill switches on live calculator pages — default ON so an outage keeps
    // every calculator up.
    [FLAG_CALCULATORS]: import.meta.env.VITE_CALCULATORS_ENABLED !== 'false',
    [FLAG_CALCULATORS_CRUT]: import.meta.env.VITE_CALCULATORS_CRUT_ENABLED !== 'false',
    [FLAG_CALCULATORS_CLAT]: import.meta.env.VITE_CALCULATORS_CLAT_ENABLED !== 'false',
    [FLAG_CALCULATORS_GRAT]: import.meta.env.VITE_CALCULATORS_GRAT_ENABLED !== 'false',
    [FLAG_CALCULATORS_QSBS]: import.meta.env.VITE_CALCULATORS_QSBS_ENABLED !== 'false',
    [FLAG_CALCULATORS_SOLAR]: import.meta.env.VITE_CALCULATORS_SOLAR_ENABLED !== 'false',
    [FLAG_CALCULATORS_IRA]: import.meta.env.VITE_CALCULATORS_IRA_ENABLED !== 'false',
    [FLAG_CALCULATORS_OIL_GAS]: import.meta.env.VITE_CALCULATORS_OIL_GAS_ENABLED !== 'false',
    [FLAG_CALCULATORS_PPLI]: import.meta.env.VITE_CALCULATORS_PPLI_ENABLED !== 'false',
    [FLAG_CALCULATORS_NGT]: import.meta.env.VITE_CALCULATORS_NGT_ENABLED !== 'false',
    [FLAG_CALCULATORS_ESTATE_TAX_BURN]: import.meta.env.VITE_CALCULATORS_ESTATE_TAX_BURN_ENABLED !== 'false',
    [FLAG_CALCULATORS_COMPARISON]: import.meta.env.VITE_CALCULATORS_COMPARISON_ENABLED !== 'false',
    [FLAG_CALCULATORS_COMPARISON_CAPITAL_GAINS]:
        import.meta.env.VITE_CALCULATORS_COMPARISON_CAPITAL_GAINS_ENABLED !== 'false',
    [FLAG_CALCULATORS_COMPARISON_ORDINARY_INCOME]:
        import.meta.env.VITE_CALCULATORS_COMPARISON_ORDINARY_INCOME_ENABLED !== 'false',
    [FLAG_CALCULATORS_COMPARISON_ESTATE]: import.meta.env.VITE_CALCULATORS_COMPARISON_ESTATE_ENABLED !== 'false',
};

const state = reactive({ flags: { ...DEFAULTS } });

export function isFeatureEnabled(flag) {
    if (!(flag in DEFAULTS)) {
        console.warn(`[feature_flags] unknown flag "${flag}"`);
        return false;
    }
    return Boolean(state.flags[flag]);
}

/**
 * Merge explicit values into the flag state. Unknown keys are ignored, and keys
 * absent from `flags` keep their current value — so callers must pass a COMPLETE
 * map (every registry key with an explicit boolean) or a flag can never be
 * turned off through here. The backend bootstrap is that complete map; PostHog's
 * own browser payload is not (it omits disabled flags), which is why nothing
 * feeds this function straight from posthog-js — see plugins/posthog.js.
 */
export function applyFeatureFlags(flags) {
    for (const [flag, value] of Object.entries(flags || {})) {
        if (flag in DEFAULTS && typeof value === 'boolean') {
            state.flags[flag] = value;
        }
    }
}

/**
 * Fetch backend-evaluated flags (GET /api/feature-flags/) — called before
 * mount from main.js. Returns posthog-js bootstrap data, or null on failure.
 * Never throws: a dead backend degrades to the compiled defaults.
 */
/**
 * Re-read the flags whenever the tab returns to the foreground (throttled).
 *
 * Without this, switching a flag off only reaches tabs that get reloaded: the
 * backend starts denying immediately, but the SPA keeps rendering whatever it
 * decided at boot, so a kill switch looks like it did nothing. posthog-js is no
 * help here — it reloads flags on init/identify, not on a timer, and its payload
 * omits disabled flags anyway. Foreground is the natural trigger: it's when
 * someone comes back to the app after flipping the switch, and it costs one
 * small request. Pass a function so the distinct id is read at refresh time,
 * not captured at setup.
 */
export function refreshFeatureFlagsOnForeground(getDistinctId = () => null, minIntervalMs = 60000) {
    let lastRefresh = Date.now();
    const maybeRefresh = () => {
        if (document.visibilityState !== 'visible') return;
        if (Date.now() - lastRefresh < minIntervalMs) return;
        lastRefresh = Date.now();
        hydrateFeatureFlags(getDistinctId());
    };
    // visibilitychange covers tab switches, focus covers window switches; the
    // throttle absorbs the cases where both fire.
    document.addEventListener('visibilitychange', maybeRefresh);
    window.addEventListener('focus', maybeRefresh);
}

export async function hydrateFeatureFlags(distinctId = null) {
    try {
        const { data } = await axios.get(`${API_ROOT}/feature-flags/`, {
            params: distinctId ? { distinct_id: distinctId } : {},
            withCredentials: true,
            timeout: 3000,
        });
        applyFeatureFlags(data.flags);
        return { distinctID: data.distinct_id, featureFlags: data.flags };
    } catch (e) {
        return null;
    }
}
